import React from "react";
import { cn } from "../../../utils/util";
import styles from "./stack.module.css";

/**
 * Column - A component for vertical layout with flexible configuration
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The content to be displayed
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Custom inline styles
 * @param {string|number} props.spacing - Space between items (e.g., "10px", 2)
 * @param {string|number} props.gap - Alternative prop for spacing (takes precedence over spacing)
 * @param {string} props.justify - Justify content (start, end, center, between, around, evenly)
 * @param {string} props.align - Align items (start, end, center, baseline, stretch)
 * @param {string} props.width - Width of the stack
 * @param {boolean} props.fullWidth - Whether to take full width
 */
const Column = ({
  children,
  className,
  style = {},
  spacing = "0",
  gap,
  justify = "start",
  align = "center",
  width,
  fullWidth = false,
  ...props
}) => {
  // Use gap prop if provided, otherwise use spacing
  const spacingValue = gap !== undefined ? gap : spacing;
  // Convert spacing value to CSS
  const cssGap = typeof spacingValue === "number" ? `${spacingValue * 4}px` : spacingValue;

  // Map justify values to CSS values
  const justifyMap = {
    start: "flex-start",
    end: "flex-end",
    center: "center",
    between: "space-between",
    around: "space-around",
    evenly: "space-evenly",
  };

  // Map align values to CSS values
  const alignMap = {
    start: "flex-start",
    end: "flex-end",
    center: "center",
    baseline: "baseline",
    stretch: "stretch",
  };

  const flexStyles = {
    display: "flex",
    flexDirection: "column",
    gap: cssGap,
    justifyContent: justifyMap[justify] || justify,
    alignItems: alignMap[align] || align,
    width: fullWidth ? "100%" : width,
    ...style,
  };

  return (
    <div className={cn(styles.column, className)} style={flexStyles} {...props}>
      {children}
    </div>
  );
};

export default Column; 